import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Callout, Select, Switch, Textarea, TextInput } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { getLocationReturnPath, openRecord, useNavigateBack } from '../../../hooks/useNavigateBack';
import { cn } from '../../../lib/utils';
import { moduleConfig } from './config';
import {
  AnswerType,
  CrewQuestionnaireQuestion,
  useCrewQuestionnaire,
  useQuestionnaireKinds,
} from './useData';

type FormState = {
  kindId: number;
  name: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
  questions: CrewQuestionnaireQuestion[];
};

const newQuestion = (sortOrder: number): CrewQuestionnaireQuestion => ({
  code: '',
  label: '',
  helpText: '',
  answerType: 'SCALE',
  scaleMin: 1,
  scaleMax: 5,
  scaleStep: 1,
  sortOrder,
  isRequired: false,
  isActive: true,
});

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const { colors } = useTheme();
  return (
    <label className="block space-y-1.5">
      <span className={cn('block text-sm font-medium', colors.text)}>{label}</span>
      {children}
    </label>
  );
};

const Form: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getLocationReturnPath(location.state);
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const editId = Number(id);
  const isEdit = Number.isInteger(editId);
  const { data, isLoading } = useCrewQuestionnaire(isEdit ? editId : 0);
  const { data: kinds } = useQuestionnaireKinds();
  const kindIdFromUrl = Number(searchParams.get('kindId'));
  const initialKindId =
    !isEdit && Number.isFinite(kindIdFromUrl) && kindIdFromUrl > 0 ? kindIdFromUrl : 0;
  const [form, setForm] = useState<FormState>({
    kindId: initialKindId,
    name: '',
    description: '',
    sortOrder: 0,
    isActive: true,
    questions: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!data) return;
    setForm({
      kindId: data.kindId,
      name: data.name,
      description: data.description ?? '',
      sortOrder: data.sortOrder,
      isActive: data.isActive,
      questions: (data.questions ?? []).map((q, index) => ({
        ...q,
        helpText: q.helpText ?? '',
        sortOrder: q.sortOrder ?? index,
      })),
    });
  }, [data]);

  useEffect(() => {
    if (isEdit || form.kindId) return;
    if (Number.isFinite(kindIdFromUrl) && kindIdFromUrl > 0) {
      setForm((old) => (old.kindId ? old : { ...old, kindId: kindIdFromUrl }));
    }
  }, [isEdit, kindIdFromUrl, form.kindId]);

  const setQuestion = (index: number, patch: Partial<CrewQuestionnaireQuestion>) => {
    setForm((old) => ({
      ...old,
      questions: old.questions.map((question, i) =>
        i === index ? { ...question, ...patch } : question,
      ),
    }));
  };

  const reorder = (index: number, direction: -1 | 1) => {
    setForm((old) => {
      const target = index + direction;
      if (target < 0 || target >= old.questions.length) return old;
      const questions = [...old.questions];
      [questions[index], questions[target]] = [questions[target], questions[index]];
      return {
        ...old,
        questions: questions.map((question, i) => ({ ...question, sortOrder: i })),
      };
    });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (
      !form.kindId ||
      !form.name.trim() ||
      form.questions.some((q) => !q.code.trim() || !q.label.trim())
    ) {
      setError('Select a kind and complete the name, code, and label for every question.');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      name: form.name.trim(),
      description: form.description.trim() || null,
      questions: form.questions.map((q, index) => ({
        ...q,
        code: q.code.trim().toUpperCase(),
        label: q.label.trim(),
        helpText: q.helpText?.trim() || null,
        sortOrder: index,
        scaleMin: q.answerType === 'SCALE' ? Number(q.scaleMin) : null,
        scaleMax: q.answerType === 'SCALE' ? Number(q.scaleMax) : null,
        scaleStep: q.answerType === 'SCALE' ? Number(q.scaleStep) : null,
      })),
    };
    try {
      const mutations = await import('../../../services/mutations');
      const saved = isEdit
        ? await mutations.updateCrewQuestionnaire(editId, payload)
        : await mutations.createCrewQuestionnaire(payload);
      openRecord(
        navigate,
        moduleConfig.module.route.detail(saved.id),
        returnPath ?? moduleConfig.module.route.list,
      );
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? moduleConfig.form.errorMessage;
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const listHref =
    form.kindId > 0
      ? `${moduleConfig.module.route.list}?kindId=${form.kindId}`
      : moduleConfig.module.route.list;
  const goBack = useNavigateBack(listHref, returnPath);

  if (isEdit && isLoading) {
    return <div className={cn('p-8 text-sm', colors.textMuted)}>Loading...</div>;
  }

  return (
    <div className={cn('min-h-full', colors.background)}>
      <div className="mx-auto max-w-5xl px-4 py-6">
        <button
          type="button"
          onClick={() =>
            isEdit
              ? openRecord(
                  navigate,
                  moduleConfig.module.route.detail(editId),
                  returnPath ?? listHref,
                )
              : goBack()
          }
          className={cn('text-sm', colors.textSecondary)}
        >
          ← Back
        </button>
        <h1 className={cn('mt-2 text-2xl font-semibold', colors.text)}>
          {isEdit ? moduleConfig.form.edit.title : moduleConfig.form.new.title}
        </h1>
        {error ? (
          <Callout variant="danger" className="mt-4">
            {error}
          </Callout>
        ) : null}
        <form onSubmit={submit} className="mt-6 space-y-6">
          <section
            className={cn(
              'grid gap-4 rounded-xl border p-5 md:grid-cols-12',
              colors.surface,
              colors.border,
            )}
          >
            <div className="md:col-span-5">
              <Field label="Kind">
                <Select
                  value={String(form.kindId || '')}
                  onChange={(e) => setForm({ ...form, kindId: Number(e.target.value) })}
                  options={[
                    { value: '', label: 'Select kind' },
                    ...kinds.map((kind) => ({ value: String(kind.id), label: kind.name })),
                  ]}
                  showClearButton={false}
                />
              </Field>
            </div>
            <div className="md:col-span-7">
              <Field label="Name">
                <TextInput
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  showClearButton={false}
                />
              </Field>
            </div>
            <div className="md:col-span-12">
              <Field label="Description">
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                />
              </Field>
            </div>
            <div className="md:col-span-3">
              <Field label="Sort order">
                <TextInput
                  type="number"
                  value={String(form.sortOrder)}
                  onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) || 0 })}
                  showClearButton={false}
                />
              </Field>
            </div>
            <div className="flex items-end md:col-span-9">
              <Switch
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                label="Active"
                description="Available for new crew assessments."
              />
            </div>
          </section>

          <section className={cn('rounded-xl border p-5', colors.surface, colors.border)}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className={cn('font-semibold', colors.text)}>Questions</h2>
                <p className={cn('text-sm', colors.textSecondary)}>
                  Questions are sent as the complete ordered list when saved.
                </p>
              </div>
              <Button
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    questions: [...form.questions, newQuestion(form.questions.length)],
                  })
                }
              >
                Add question
              </Button>
            </div>
            <div className="space-y-4">
              {form.questions.map((question, index) => (
                <QuestionCard
                  key={question.id ?? index}
                  question={question}
                  index={index}
                  total={form.questions.length}
                  setQuestion={setQuestion}
                  reorder={reorder}
                  remove={() =>
                    setForm({
                      ...form,
                      questions: form.questions
                        .filter((_, i) => i !== index)
                        .map((q, i) => ({ ...q, sortOrder: i })),
                    })
                  }
                />
              ))}
            </div>
          </section>

          <div className="flex gap-3">
            <Button type="submit" disabled={saving} loading={saving}>
              {isEdit ? moduleConfig.form.edit.submitText : moduleConfig.form.new.submitText}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                isEdit
                  ? openRecord(
                      navigate,
                      moduleConfig.module.route.detail(editId),
                      returnPath ?? listHref,
                    )
                  : goBack()
              }
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const QuestionCard: React.FC<{
  question: CrewQuestionnaireQuestion;
  index: number;
  total: number;
  setQuestion: (i: number, patch: Partial<CrewQuestionnaireQuestion>) => void;
  reorder: (i: number, d: -1 | 1) => void;
  remove: () => void;
}> = ({ question, index, total, setQuestion, reorder, remove }) => {
  const { colors } = useTheme();
  return (
    <div className={cn('rounded-lg border p-4', colors.border, colors.headerBg)}>
      <div className="mb-3 flex justify-between gap-2">
        <strong className={cn('text-sm', colors.text)}>Question {index + 1}</strong>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!index}
            onClick={() => reorder(index, -1)}
            aria-label="Move up"
          >
            ↑
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={index === total - 1}
            onClick={() => reorder(index, 1)}
            aria-label="Move down"
          >
            ↓
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={remove} className="text-red-600 hover:text-red-700">
            Remove
          </Button>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-12">
        <div className="md:col-span-3">
          <Field label="Code">
            <TextInput
              value={question.code}
              onChange={(e) => setQuestion(index, { code: e.target.value.toUpperCase() })}
              showClearButton={false}
            />
          </Field>
        </div>
        <div className="md:col-span-5">
          <Field label="Label">
            <TextInput
              value={question.label}
              onChange={(e) => setQuestion(index, { label: e.target.value })}
              showClearButton={false}
            />
          </Field>
        </div>
        <div className="md:col-span-4">
          <Field label="Answer type">
            <Select
              value={question.answerType}
              onChange={(e) => setQuestion(index, { answerType: e.target.value as AnswerType })}
              options={[
                { value: 'SCALE', label: 'Scale' },
                { value: 'YES_NO_NA', label: 'Yes / No / N/A' },
                { value: 'TEXT', label: 'Text' },
              ]}
              showClearButton={false}
            />
          </Field>
        </div>
        <div className="md:col-span-12">
          <Field label="Help text">
            <Textarea
              value={question.helpText ?? ''}
              onChange={(e) => setQuestion(index, { helpText: e.target.value })}
              rows={2}
            />
          </Field>
        </div>
        {question.answerType === 'SCALE' ? (
          <ScaleFields question={question} index={index} setQuestion={setQuestion} />
        ) : null}
        <div className="md:col-span-6">
          <Switch
            checked={question.isRequired}
            onChange={(e) => setQuestion(index, { isRequired: e.target.checked })}
            label="Required"
          />
        </div>
        <div className="md:col-span-6">
          <Switch
            checked={question.isActive}
            onChange={(e) => setQuestion(index, { isActive: e.target.checked })}
            label="Active"
          />
        </div>
      </div>
    </div>
  );
};

const ScaleFields: React.FC<{
  question: CrewQuestionnaireQuestion;
  index: number;
  setQuestion: (i: number, patch: Partial<CrewQuestionnaireQuestion>) => void;
}> = ({ question, index, setQuestion }) => {
  const { colors } = useTheme();
  return (
    <>
      <div className="md:col-span-12 flex flex-wrap gap-2">
        {[
          [1, 5, 1],
          [0, 100, 5],
          [0, 100, 10],
        ].map(([min, max, step]) => (
          <button
            key={`${min}-${max}-${step}`}
            type="button"
            onClick={() => setQuestion(index, { scaleMin: min, scaleMax: max, scaleStep: step })}
            className={cn(
              'rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
              colors.border,
              colors.textSecondary,
              colors.interactiveHover,
            )}
          >
            {min}–{max} / {step}
          </button>
        ))}
      </div>
      {(['scaleMin', 'scaleMax', 'scaleStep'] as const).map((key) => (
        <div className="md:col-span-4" key={key}>
          <Field label={key === 'scaleMin' ? 'Minimum' : key === 'scaleMax' ? 'Maximum' : 'Step'}>
            <TextInput
              type="number"
              value={String(question[key] ?? '')}
              onChange={(e) =>
                setQuestion(index, {
                  [key]: Number(e.target.value),
                } as Partial<CrewQuestionnaireQuestion>)
              }
              showClearButton={false}
            />
          </Field>
        </div>
      ))}
    </>
  );
};

export default Form;
